/**
 * StudentReportView.js
 *
 * Interactive student growth report — shows milestones, study plan history,
 * quiz performance, Bloom's progress, and an AI-generated personal message.
 *
 * Supports:
 *   • On-screen interactive view (scrollable dialog)
 *   • "Download PDF" — html2canvas → jsPDF
 *
 * Props:
 *   open         {boolean}
 *   onClose      {function}
 *   userId       {string}
 *   user         {object}    Firebase user (for display name / email)
 *   events       {Array}     Journey events (from LearningJourneyView)
 *   studyPlans   {Array}     All study plans from Firestore
 */

import React, { useRef, useState, useMemo, useEffect } from 'react';
import {
  Box, Dialog, DialogContent, DialogTitle, IconButton, Typography,
  Paper, Chip, Button, Divider, CircularProgress, LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  EmojiEvents as TrophyIcon,
  CalendarMonth as PlanIcon,
  FactCheck as QuizIcon,
  AutoAwesome as AIIcon,
  LocalFireDepartment as FireIcon,
  Hub as HubIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  TrendingUp as GrowthIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  RadialLinearScale, PointElement, LineElement, Filler,
  ArcElement, CategoryScale, LinearScale, BarElement,
  Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Radar, Bar, Line } from 'react-chartjs-2';
import { generateInsights } from '../services/learningJourneyService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

ChartJS.register(
  RadialLinearScale, PointElement, LineElement, Filler,
  ArcElement, CategoryScale, LinearScale, BarElement,
  ChartTooltip, Legend,
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtTime(ms = 0) {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function planStepCount(plan) {
  if (!plan?.plan?.days) return 0;
  return plan.plan.days.reduce((s, d) => s + (d.steps?.length || 0), 0);
}

function planCompletion(plan) {
  const total = planStepCount(plan);
  if (!total) return 0;
  return Math.round(((plan.completedStepIds?.length || 0) / total) * 100);
}

function planTimeMs(plan) {
  if (!plan?.stepTimes) return 0;
  return Object.values(plan.stepTimes).reduce((s, v) => s + (v || 0), 0);
}

function planQuizScore(plan) {
  const r = plan?.quizResults;
  if (!r?.pct) return null;
  return r.pct;
}

function gradeColor(grade) {
  if (!grade) return '#94a3b8';
  if (grade.startsWith('A')) return '#10b981';
  if (grade.startsWith('B')) return '#3b82f6';
  if (grade.startsWith('C')) return '#f59e0b';
  return '#ef4444';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon, label, value, color = '#6366f1' }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 0, p: 1.75, borderRadius: 2,
      border: '1px solid', borderColor: `${color}25`,
      bgcolor: `${color}08`, textAlign: 'center',
    }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>{label}</Typography>
    </Paper>
  );
}

function MilestoneChip({ icon, label, sub, color }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.5, py: 1, borderRadius: 2,
      border: '1px solid', borderColor: `${color}30`,
      bgcolor: `${color}08`, minWidth: 0,
    }}>
      <Typography sx={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', lineHeight: 1.2 }} noWrap>{label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }} noWrap>{sub}</Typography>
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function StudentReportView({ open, onClose, userId, user, events = [], studyPlans = [] }) {
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  // ── Derived metrics ──────────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const aiQueries = events.filter(e => e.type === 'ai_query');
    const quizEvents = events.filter(e => e.type === 'quiz_complete');

    const completedPlans = studyPlans.filter(p => p.phase === 'complete' || planCompletion(p) === 100);
    const totalStudyMs = studyPlans.reduce((s, p) => s + planTimeMs(p), 0);
    const avgQuizScore = studyPlans.reduce((acc, p, _, arr) => {
      const sc = planQuizScore(p);
      return sc !== null ? acc + sc / arr.length : acc;
    }, 0);

    // Subject breakdown
    const subjectMap = {};
    studyPlans.forEach(p => {
      const subj = p.plan?.subject || 'General';
      if (!subjectMap[subj]) subjectMap[subj] = { count: 0, totalScore: 0, scoreCount: 0 };
      subjectMap[subj].count++;
      const sc = planQuizScore(p);
      if (sc !== null) { subjectMap[subj].totalScore += sc; subjectMap[subj].scoreCount++; }
    });

    // Quiz score trend (by plan creation date, newest last)
    const plansByDate = [...studyPlans].reverse();
    const scoreTrend = plansByDate.map(p => ({
      label: (p.sourceName || p.plan?.title || 'Plan').slice(0, 18),
      score: planQuizScore(p),
    })).filter(x => x.score !== null);

    // Milestones
    const milestones = [];
    if (studyPlans.length > 0) milestones.push({ icon: '🚀', label: 'First Study Plan', sub: 'Journey begun!', color: '#6366f1' });
    if (completedPlans.length > 0) milestones.push({ icon: '✅', label: 'Plan Completed', sub: `${completedPlans.length} plan${completedPlans.length > 1 ? 's' : ''}`, color: '#10b981' });
    const best = studyPlans.reduce((b, p) => { const sc = planQuizScore(p); return (sc !== null && sc > (b || 0)) ? sc : b; }, null);
    if (best !== null && best >= 80) milestones.push({ icon: '🏆', label: `Quiz Score ${best}%`, sub: 'Excellence achieved!', color: '#f59e0b' });
    if (aiQueries.length >= 20) milestones.push({ icon: '🤖', label: `${aiQueries.length} AI Queries`, sub: 'Active learner', color: '#8b5cf6' });
    if (quizEvents.length >= 5) milestones.push({ icon: '🎯', label: `${quizEvents.length} Quizzes Done`, sub: 'Consistent practice', color: '#ef4444' });
    if (Object.keys(subjectMap).length >= 3) milestones.push({ icon: '📚', label: `${Object.keys(subjectMap).length} Subjects`, sub: 'Broad learner', color: '#06b6d4' });

    // Bloom's from quiz events
    const bloomsTotal = { Remember: 0, Understand: 0, Apply: 0, Analyse: 0, Evaluate: 0, Create: 0 };
    quizEvents.forEach(e => {
      if (e.byBlooms) Object.entries(e.byBlooms).forEach(([k, v]) => { if (bloomsTotal[k] !== undefined) bloomsTotal[k] += v || 0; });
    });

    return {
      aiQueriesCount: aiQueries.length,
      completedPlansCount: completedPlans.length,
      totalStudyMs,
      avgQuizScore: Math.round(avgQuizScore),
      subjectMap,
      scoreTrend,
      milestones,
      bloomsTotal,
      totalPlans: studyPlans.length,
    };
  }, [events, studyPlans]);

  // ── Load AI insights ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || aiInsights || !events.length) return;
    setLoadingInsights(true);
    const aiQueryEvents = events.filter(e => e.type === 'ai_query');
    generateInsights(events, {}).then(result => {
      setAiInsights(result);
    }).catch(() => {}).finally(() => setLoadingInsights(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── PDF Export ────────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = pdfHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
      while (heightLeft > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }
      const name = user?.displayName?.split(' ')[0] || 'Student';
      pdf.save(`Ekamanam_Report_${name}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const CHART_OPTS = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };
  const accent = '#6366f1';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { maxHeight: '94vh', borderRadius: 3 } }}>

      {/* Sticky toolbar */}
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <SchoolIcon sx={{ color: accent }} />
        <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>Student Growth Report</Typography>
        <Button
          variant="contained" startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPDF} disabled={exporting}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' } }}
        >
          {exporting ? 'Exporting…' : 'Download PDF'}
        </Button>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        {/* ── Printable content ── */}
        <Box ref={reportRef} sx={{ p: { xs: 2.5, sm: 3 }, bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* ── HEADER ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 2, borderBottom: '2px solid #e11d48' }}>
            <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: '#e11d4815', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>
              🎓
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#1e293b', lineHeight: 1.2 }}>
                {user?.displayName || user?.email?.split('@')[0] || 'Student'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ekamanam Student Growth Report · Generated {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h4" fontWeight={800} sx={{ color: '#e11d48', lineHeight: 1 }}>ekamanam</Typography>
              <Typography variant="caption" color="text.secondary">ఏకమనం · Art of Focused Learning</Typography>
            </Box>
          </Box>

          {/* ── SUMMARY STATS ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Overview</Typography>
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <StatCard icon={<PlanIcon />} label="Study Plans" value={metrics.totalPlans} color="#6366f1" />
              <StatCard icon={<CheckIcon />} label="Completed" value={metrics.completedPlansCount} color="#10b981" />
              <StatCard icon={<QuizIcon />} label="Avg Quiz Score" value={metrics.avgQuizScore ? `${metrics.avgQuizScore}%` : '—'} color="#ef4444" />
              <StatCard icon={<AIIcon />} label="AI Queries" value={metrics.aiQueriesCount} color="#8b5cf6" />
            </Box>
          </Box>

          {/* ── MILESTONES ── */}
          {metrics.milestones.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>🏆 Growth Milestones</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {metrics.milestones.map((m, i) => (
                  <MilestoneChip key={i} {...m} />
                ))}
              </Box>
            </Box>
          )}

          {/* ── STUDY PLAN HISTORY ── */}
          {studyPlans.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>📅 Study Plan History</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {studyPlans.slice(0, 8).map(p => {
                  const pct = planCompletion(p);
                  const score = planQuizScore(p);
                  const grade = p.analytics?.grade;
                  const timeMs = planTimeMs(p);
                  const isComplete = p.phase === 'complete' || pct === 100;
                  return (
                    <Box key={p.id} sx={{
                      p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                            {p.plan?.title || p.sourceName || 'Untitled Plan'}
                          </Typography>
                          <Chip
                            label={isComplete ? 'Complete' : 'In Progress'}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: isComplete ? '#10b98115' : '#f59e0b15',
                              color: isComplete ? '#10b981' : '#f59e0b' }}
                          />
                        </Box>
                        <LinearProgress
                          variant="determinate" value={pct}
                          sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': { bgcolor: isComplete ? '#10b981' : '#6366f1' } }}
                        />
                      </Box>
                      {grade && (
                        <Chip label={grade} size="small" sx={{ fontWeight: 800, fontSize: '0.75rem', bgcolor: `${gradeColor(grade)}18`, color: gradeColor(grade), height: 22 }} />
                      )}
                      {score !== null && (
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#ef4444', minWidth: 36, textAlign: 'right' }}>{score}%</Typography>
                      )}
                      {timeMs > 0 && (
                        <Typography variant="caption" color="text.disabled" sx={{ minWidth: 40, textAlign: 'right' }}>{fmtTime(timeMs)}</Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── QUIZ SCORE TREND ── */}
          {metrics.scoreTrend.length >= 2 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>📈 Quiz Score Trend</Typography>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: 180 }}>
                <Line
                  data={{
                    labels: metrics.scoreTrend.map(x => x.label),
                    datasets: [{
                      label: 'Quiz Score %',
                      data: metrics.scoreTrend.map(x => x.score),
                      borderColor: '#6366f1', backgroundColor: '#6366f118',
                      fill: true, tension: 0.35, pointRadius: 4,
                    }],
                  }}
                  options={{ ...CHART_OPTS, scales: { y: { min: 0, max: 100 } } }}
                />
              </Paper>
            </Box>
          )}

          {/* ── SUBJECT COVERAGE ── */}
          {Object.keys(metrics.subjectMap).length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>📚 Subject Coverage</Typography>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: 160 }}>
                <Bar
                  data={{
                    labels: Object.keys(metrics.subjectMap),
                    datasets: [{
                      label: 'Plans',
                      data: Object.values(metrics.subjectMap).map(v => v.count),
                      backgroundColor: ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#e11d48', '#8b5cf6', '#14b8a6'].slice(0, Object.keys(metrics.subjectMap).length),
                      borderRadius: 4,
                    }],
                  }}
                  options={CHART_OPTS}
                />
              </Paper>
            </Box>
          )}

          {/* ── BLOOM'S RADAR ── */}
          {Object.values(metrics.bloomsTotal).some(v => v > 0) && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>🧠 Bloom's Taxonomy Progress</Typography>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: 200, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: 200 }}>
                  <Radar
                    data={{
                      labels: Object.keys(metrics.bloomsTotal),
                      datasets: [{
                        label: 'Questions',
                        data: Object.values(metrics.bloomsTotal),
                        backgroundColor: '#6366f130', borderColor: '#6366f1',
                        pointBackgroundColor: '#6366f1',
                      }],
                    }}
                    options={{ ...CHART_OPTS, scales: { r: { beginAtZero: true } } }}
                  />
                </Box>
              </Paper>
            </Box>
          )}

          {/* ── AI INSIGHTS ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>✨ AI Learning Insights</Typography>
            {loadingInsights ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Generating personalised insights…</Typography>
              </Box>
            ) : aiInsights ? (
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: '#6366f125', bgcolor: '#6366f108' }}>
                {aiInsights.strengths?.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="#10b981" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Strengths</Typography>
                    {aiInsights.strengths.map((s, i) => (
                      <Typography key={i} variant="body2" sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                        <span>✓</span>{s}
                      </Typography>
                    ))}
                  </Box>
                )}
                {aiInsights.improvements?.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="#f59e0b" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Areas to Improve</Typography>
                    {aiInsights.improvements.map((s, i) => (
                      <Typography key={i} variant="body2" sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                        <span>→</span>{s}
                      </Typography>
                    ))}
                  </Box>
                )}
                {aiInsights.message && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', borderTop: '1px solid', borderColor: 'divider', pt: 1.25, mt: 1 }}>
                    {aiInsights.message}
                  </Typography>
                )}
              </Paper>
            ) : (
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="text.disabled">
                  {events.length === 0 ? 'Start using Ekamanam to generate AI insights.' : 'Insights could not be loaded.'}
                </Typography>
              </Paper>
            )}
          </Box>

          {/* ── FOOTER ── */}
          <Box sx={{ textAlign: 'center', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">
              Generated by Ekamanam · ekamanam.com · ఏకమనం — The Art of Focused Learning
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
